import React, { useState, useEffect, useRef, useMemo, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAuthInfo } from '@/utils/auth'
import { clearSession, ensureProjectContext } from '@/utils/session'
import {
  getCouponUsedStatsDaily,
  getLastNDaysExclusiveRange,
  getMembersStatsCount,
  getMembersStatsDaily,
  getMonthToDateExclusiveRange,
  getRewardStatsDaily,
  getTodayString,
  addDaysToDateString,
  calcChangePercent,
  findDailyCountByDate,
  sumDailyCounts
} from '@/api/dashboard'
import { createProjectCustomer, searchMember } from '@/api/member'
import {
  isMemberSearchKeyword,
  isSignupPhoneKeyword,
  MEMBER_KEYWORD_MAX_LENGTH
} from '@/utils/phone'
import { getProjectContext, getProjectTimezone } from '@/utils/projectContext'
import {
  buildCustomerCreateRequest,
  buildSimpleSignupConfirmMessage,
  canCreateGuestCustomer,
  MEMBER_NOT_FOUND_MESSAGE,
  MEMBER_WITHDRAWING_MESSAGE,
  needsSignupPhoneConfirm,
  resolveNavigateAfterCreate
} from '@/utils/memberSignup'
import Sparkline from '@/components/Sparkline'
import StatDetailSheet, { type StatDetailType } from '@/components/StatDetailSheet'
import styles from './Dashboard.module.css'

/** 통계 카드 한 건 타입 */
interface StatItem {
  value: string
  changePercent: number | null
  changeLabel?: string
  isPositive: boolean | null
}

/** 카드 하단 스파크라인·부가 문구 */
interface StatTrend {
  values: number[]
  meta: string
}

const STAT_TREND_COLORS = {
  newMember: '#6B73FF',
  points: '#28c76f',
  coupon: '#ff9f43'
} as const

/** 화면용 목 데이터 - 사용자명 등 API 연동 전 */
const MOCK_USER = { name: '김매니저' }

function buildStatFromPercent(
  value: string,
  todayCount: number,
  yesterdayCount: number
): StatItem {
  const percent = calcChangePercent(todayCount, yesterdayCount)
  return {
    value,
    changePercent: percent,
    isPositive: percent === null ? null : percent >= 0
  }
}

function emptyTrend(meta = '추이 데이터 없음'): StatTrend {
  return { values: [], meta }
}

function buildWeekTrendMeta(
  yesterdayCount: number,
  weekSum: number,
  unit: '명' | '건'
): string {
  return `어제 ${yesterdayCount}${unit} · 이번 주 ${weekSum}${unit}`
}

export default function Dashboard() {
  const navigate = useNavigate()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [storeName, setStoreName] = useState<string>('로딩중...')
  const [outletCode, setOutletCode] = useState<string>('-')
  const [userName, setUserName] = useState<string>('로딩중...')

  const [statsLoading, setStatsLoading] = useState(true)
  const [stats, setStats] = useState<{
    newMember: StatItem
    points: StatItem
    coupon: StatItem
    totalMember: StatItem
  } | null>(null)
  const [trendsLoading, setTrendsLoading] = useState(true)
  const [trends, setTrends] = useState<{
    newMember: StatTrend
    points: StatTrend
    coupon: StatTrend
    totalMember: StatTrend
  } | null>(null)

  const [searchValue, setSearchValue] = useState('')
  const [searchSubmitting, setSearchSubmitting] = useState(false)
  const [errorToast, setErrorToast] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailType, setDetailType] = useState<StatDetailType | null>(null)
  const [projectTimezone, setProjectTimezone] = useState<string | null>(() =>
    getProjectTimezone()
  )

  const today = useMemo(
    () => getTodayString(projectTimezone),
    [projectTimezone]
  )
  const yesterday = useMemo(() => addDaysToDateString(today, -1), [today])
  const weekRange = useMemo(() => getLastNDaysExclusiveRange(today, 7), [today])
  const monthRange = useMemo(() => getMonthToDateExclusiveRange(today), [today])

  // 매장/사용자 정보 (로그인 시 저장된 값 사용)
  useEffect(() => {
    const authInfo = getAuthInfo()
    if (authInfo?.shopName != null) setStoreName(authInfo.shopName)
    if (authInfo?.outletCode != null) setOutletCode(authInfo.outletCode)
    setUserName(MOCK_USER.name)
  }, [])

  // ProjectContext 없으면 lookup으로 복구 (새로고침 등)
  useEffect(() => {
    let cancelled = false
    ensureProjectContext()
      .then(() => {
        if (cancelled) return
        setProjectTimezone(getProjectTimezone())
      })
      .catch((err) => {
        if (cancelled) return
        setErrorToast(
          err instanceof Error ? err.message : '프로젝트 정보를 불러오지 못했습니다.'
        )
      })
    return () => {
      cancelled = true
    }
  }, [])

  // 통계: 회원 count + 회원/적립/쿠폰 daily(7일)
  useEffect(() => {
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()

    if (!projectCode || !shopCode) {
      setStoreName(storeName => (storeName === '로딩중...' ? '매장 정보 없음' : storeName))
      setStats({
        newMember: { value: '-', changePercent: null, isPositive: null },
        points: { value: '-', changePercent: null, isPositive: null },
        coupon: { value: '-', changePercent: null, isPositive: null },
        totalMember: { value: '-', changePercent: null, isPositive: null, changeLabel: '전체' }
      })
      setTrends({
        newMember: emptyTrend(),
        points: emptyTrend(),
        coupon: emptyTrend(),
        totalMember: emptyTrend()
      })
      setStatsLoading(false)
      setTrendsLoading(false)
      return
    }

    let cancelled = false

    const isAuthError = (err: Error) =>
      /401|403|인증|로그인/.test(err.message)

    const handleRejected = (result: PromiseRejectedResult): boolean => {
      const err = result.reason as Error
      if (isAuthError(err)) {
        setStatsLoading(false)
        setTrendsLoading(false)
        clearSession()
        navigate('/auth/login')
        return true
      }
      setErrorToast(err.message)
      return false
    }

    const load = async () => {
      const [
        membersTotalResult,
        membersMonthCountResult,
        membersDailyResult,
        rewardDailyResult,
        couponDailyResult
      ] = await Promise.allSettled([
        getMembersStatsCount(projectCode, shopCode, {
          startDate: '',
          endDate: '',
          timezone: projectTimezone
        }),
        getMembersStatsCount(projectCode, shopCode, {
          ...monthRange,
          timezone: projectTimezone
        }),
        getMembersStatsDaily(projectCode, shopCode, {
          ...weekRange,
          timezone: projectTimezone
        }),
        getRewardStatsDaily(projectCode, shopCode, {
          ...weekRange,
          timezone: projectTimezone
        }),
        getCouponUsedStatsDaily(projectCode, shopCode, {
          ...weekRange,
          timezone: projectTimezone
        })
      ])

      if (cancelled) return

      for (const result of [
        membersTotalResult,
        membersMonthCountResult,
        membersDailyResult,
        rewardDailyResult,
        couponDailyResult
      ]) {
        if (result.status === 'rejected' && handleRejected(result)) return
      }

      const membersTotal =
        membersTotalResult.status === 'fulfilled' ? membersTotalResult.value : null
      const membersMonthCount =
        membersMonthCountResult.status === 'fulfilled'
          ? membersMonthCountResult.value
          : null
      const membersDaily =
        membersDailyResult.status === 'fulfilled' ? membersDailyResult.value : null
      const rewardDaily =
        rewardDailyResult.status === 'fulfilled' ? rewardDailyResult.value : null
      const couponDaily =
        couponDailyResult.status === 'fulfilled' ? couponDailyResult.value : null

      const todayNewCount = membersDaily
        ? findDailyCountByDate(membersDaily.points, today, (p) => p.newCount)
        : 0
      const yesterdayNewCount = membersDaily
        ? findDailyCountByDate(membersDaily.points, yesterday, (p) => p.newCount)
        : 0
      const memberWeekValues = membersDaily?.points.map((p) => p.newCount) ?? []
      const memberWeekSum = membersDaily
        ? sumDailyCounts(membersDaily.points, (p) => p.newCount)
        : 0

      const todayEarnCount = rewardDaily
        ? findDailyCountByDate(rewardDaily.points, today, (p) => p.earnCount)
        : 0
      const yesterdayEarnCount = rewardDaily
        ? findDailyCountByDate(rewardDaily.points, yesterday, (p) => p.earnCount)
        : 0
      const earnWeekValues = rewardDaily?.points.map((p) => p.earnCount) ?? []
      const earnWeekSum = rewardDaily
        ? sumDailyCounts(rewardDaily.points, (p) => p.earnCount)
        : 0

      const todayCouponCount = couponDaily
        ? findDailyCountByDate(couponDaily.points, today, (p) => p.count)
        : 0
      const yesterdayCouponCount = couponDaily
        ? findDailyCountByDate(couponDaily.points, yesterday, (p) => p.count)
        : 0
      const couponWeekValues = couponDaily?.points.map((p) => p.count) ?? []
      const couponWeekSum = couponDaily
        ? sumDailyCounts(couponDaily.points, (p) => p.count)
        : 0

      setStats({
        newMember: membersDaily
          ? buildStatFromPercent(
              `${todayNewCount}명`,
              todayNewCount,
              yesterdayNewCount
            )
          : { value: '-', changePercent: null, isPositive: null },
        points: rewardDaily
          ? buildStatFromPercent(
              `${todayEarnCount}건`,
              todayEarnCount,
              yesterdayEarnCount
            )
          : { value: '-', changePercent: null, isPositive: null },
        coupon: couponDaily
          ? buildStatFromPercent(
              `${todayCouponCount}건`,
              todayCouponCount,
              yesterdayCouponCount
            )
          : { value: '-', changePercent: null, isPositive: null },
        totalMember: membersTotal
          ? {
              value: `${membersTotal.count}명`,
              changePercent: null,
              isPositive: null,
              changeLabel: '전체'
            }
          : { value: '-', changePercent: null, isPositive: null, changeLabel: '전체' }
      })
      setStatsLoading(false)

      setTrends({
        newMember: membersDaily
          ? {
              values: memberWeekValues,
              meta: buildWeekTrendMeta(yesterdayNewCount, memberWeekSum, '명')
            }
          : emptyTrend(),
        points: rewardDaily
          ? {
              values: earnWeekValues,
              meta: buildWeekTrendMeta(yesterdayEarnCount, earnWeekSum, '건')
            }
          : emptyTrend(),
        coupon: couponDaily
          ? {
              values: couponWeekValues,
              meta: buildWeekTrendMeta(yesterdayCouponCount, couponWeekSum, '건')
            }
          : emptyTrend(),
        totalMember: {
          values: [],
          meta:
            membersMonthCount != null
              ? `이번 달 신규 +${membersMonthCount.count}`
              : '이번 달 신규 데이터 없음'
        }
      })
      setTrendsLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [today, yesterday, weekRange, monthRange, projectTimezone, navigate])

  // 검색창 포커스
  useEffect(() => {
    searchInputRef.current?.focus()
  }, [])

  const showError = (message: string) => {
    setErrorToast(message)
    const t = setTimeout(() => setErrorToast(null), 3000)
    return () => clearTimeout(t)
  }

  const handleStatClick = (type: StatDetailType) => {
    setDetailType(type)
    setDetailOpen(true)
  }

  const handleDetailClose = () => {
    setDetailOpen(false)
    setDetailType(null)
  }

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = searchValue.trim()
    if (!trimmed) {
      showError('바코드 또는 전화번호를 입력해주세요.')
      return
    }
    if (!isMemberSearchKeyword(trimmed)) {
      showError(`숫자만 입력해주세요. (최대 ${MEMBER_KEYWORD_MAX_LENGTH}자리)`)
      return
    }
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const projectContext = getProjectContext()
    if (!projectCode || !shopCode) {
      showError('매장 정보가 없습니다. 다시 로그인해 주세요.')
      return
    }
    if (!projectContext) {
      showError('프로젝트 정보가 없습니다. 다시 로그인해 주세요.')
      return
    }
    setSearchSubmitting(true)
    try {
      const data = await searchMember(projectCode, projectContext, trimmed)

      if (data.found) {
        if (data.deleted === 1) {
          showError(MEMBER_WITHDRAWING_MESSAGE)
          return
        }
        navigate('/dashboard/member', {
          state: {
            customerCode: data.customerCode,
            nickname: data.nickname,
            status: data.status
          }
        })
        return
      }

      if (!canCreateGuestCustomer(data, projectContext)) {
        showError(MEMBER_NOT_FOUND_MESSAGE)
        return
      }

      const createScope = data.createScope!
      let createBody
      try {
        createBody = buildCustomerCreateRequest(projectContext, data, trimmed)
      } catch (buildErr) {
        showError(buildErr instanceof Error ? buildErr.message : MEMBER_NOT_FOUND_MESSAGE)
        return
      }

      if (createScope === 'BRAND') {
        const created = await createProjectCustomer(projectCode, createBody)
        const nav = resolveNavigateAfterCreate(createScope, data, created)
        navigate('/dashboard/member', { state: nav })
        return
      }

      if (!needsSignupPhoneConfirm(createScope) || !isSignupPhoneKeyword(trimmed)) {
        showError(MEMBER_NOT_FOUND_MESSAGE)
        return
      }

      if (!window.confirm(buildSimpleSignupConfirmMessage(trimmed))) {
        return
      }

      const created = await createProjectCustomer(projectCode, createBody)
      const nav = resolveNavigateAfterCreate(createScope, data, created)
      navigate('/dashboard/member', { state: nav })
      return
    } catch (err) {
      showError(err instanceof Error ? err.message : '회원 조회에 실패했습니다.')
    } finally {
      setSearchSubmitting(false)
    }
  }

  const handleLogout = () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      clearSession()
      navigate('/auth/login')
    }
  }

  const renderStatChange = (item: StatItem) => {
    if (item.changePercent === null && !item.changeLabel) return null
    const changeClass =
      item.isPositive === null
        ? styles.statChangeNeutral
        : item.isPositive
          ? styles.statChangePositive
          : styles.statChangeNegative
    const arrow = item.isPositive === true ? '▲' : item.isPositive === false ? '▼' : '-'
    const text = item.changeLabel ?? `${Math.abs(item.changePercent ?? 0)}%`
    return (
      <div className={`${styles.statChange} ${changeClass}`}>
        <span className={styles.changeArrow}>{arrow}</span>
        <span>{text}</span>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.logo}>S</div>
          <div className={styles.storeInfo}>
            <div className={styles.storeName}>{storeName}</div>
            <div className={styles.storeCode}>{outletCode}</div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.userInfo}>
            <div className={styles.userIcon}>👤</div>
            <div className={styles.userName}>{userName}</div>
          </div>
          <button type="button" className={styles.logoutButton} onClick={handleLogout}>
            로그아웃
          </button>
        </div>
      </header>

      <main className={styles.mainContainer}>
        <section className={styles.statsSection}>
          <div
            className={styles.statCard}
            onClick={() => handleStatClick('new-member')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleStatClick('new-member')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>오늘 신규 회원</div>
              <div className={styles.statHeaderRight}>
                <span className={styles.statDetailHint} aria-hidden>
                  자세히 ›
                </span>
                <div className={`${styles.statIcon} ${styles.statIconNewMember}`}>👥</div>
              </div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.newMember.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.newMember)}
            <div className={styles.statTrend}>
              {trendsLoading ? (
                <span className={styles.loading} />
              ) : (
                <>
                  <Sparkline
                    values={trends?.newMember.values ?? []}
                    color={STAT_TREND_COLORS.newMember}
                    label="최근 7일 신규 회원 추이"
                  />
                  <p className={styles.statMeta}>{trends?.newMember.meta}</p>
                </>
              )}
            </div>
          </div>

          <div
            className={styles.statCard}
            onClick={() => handleStatClick('points')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleStatClick('points')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>오늘 적립 현황</div>
              <div className={styles.statHeaderRight}>
                <span className={styles.statDetailHint} aria-hidden>
                  자세히 ›
                </span>
                <div className={`${styles.statIcon} ${styles.statIconPoints}`}>💰</div>
              </div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.points.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.points)}
            <div className={styles.statTrend}>
              {trendsLoading ? (
                <span className={styles.loading} />
              ) : (
                <>
                  <Sparkline
                    values={trends?.points.values ?? []}
                    color={STAT_TREND_COLORS.points}
                    label="최근 7일 적립 추이"
                  />
                  <p className={styles.statMeta}>{trends?.points.meta}</p>
                </>
              )}
            </div>
          </div>

          <div
            className={styles.statCard}
            onClick={() => handleStatClick('coupon')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleStatClick('coupon')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>오늘 쿠폰 사용</div>
              <div className={styles.statHeaderRight}>
                <span className={styles.statDetailHint} aria-hidden>
                  자세히 ›
                </span>
                <div className={`${styles.statIcon} ${styles.statIconCoupon}`}>🎟️</div>
              </div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.coupon.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.coupon)}
            <div className={styles.statTrend}>
              {trendsLoading ? (
                <span className={styles.loading} />
              ) : (
                <>
                  <Sparkline
                    values={trends?.coupon.values ?? []}
                    color={STAT_TREND_COLORS.coupon}
                    label="최근 7일 쿠폰 사용 추이"
                  />
                  <p className={styles.statMeta}>{trends?.coupon.meta}</p>
                </>
              )}
            </div>
          </div>

          <div className={`${styles.statCard} ${styles.statCardStatic}`}>
            <div className={styles.statHeader}>
              <div className={styles.statTitle}>전체 회원</div>
              <div className={`${styles.statIcon} ${styles.statIconTotalMember}`}>📊</div>
            </div>
            <div className={styles.statValue}>
              {statsLoading ? <span className={styles.loading} /> : stats?.totalMember.value}
            </div>
            {!statsLoading && stats && renderStatChange(stats.totalMember)}
            <div className={styles.statTrend}>
              {trendsLoading ? (
                <span className={styles.loading} />
              ) : (
                <p className={styles.statMeta}>{trends?.totalMember.meta}</p>
              )}
            </div>
          </div>
        </section>

        <section className={styles.searchSection}>
          <h2 className={styles.searchTitle}>회원 조회</h2>
          <form className={styles.searchForm} onSubmit={handleSearch}>
            <div className={styles.searchInputWrapper}>
              <span className={styles.searchIcon} aria-hidden>🔍</span>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="바코드 또는 전화번호를 입력하세요"
                autoComplete="off"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={MEMBER_KEYWORD_MAX_LENGTH}
                value={searchValue}
                onChange={(e) =>
                  setSearchValue(
                    e.target.value.replace(/\D/g, '').slice(0, MEMBER_KEYWORD_MAX_LENGTH)
                  )
                }
              />
            </div>
            <button type="submit" className={styles.searchButton} disabled={searchSubmitting}>
              {searchSubmitting ? '조회 중...' : '회원 조회'}
            </button>
          </form>
          <p className={styles.searchHint}>* 바코드 스캔 또는 전화번호 입력이 가능합니다</p>
        </section>
      </main>

      {errorToast && (
        <div className={styles.errorToast} role="alert">
          {errorToast}
          <button type="button" className={styles.errorToastClose} onClick={() => setErrorToast(null)} aria-label="닫기">
            ×
          </button>
        </div>
      )}

      <StatDetailSheet open={detailOpen} type={detailType} onClose={handleDetailClose} />
    </div>
  )
}
